import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { PlusIcon, SendIcon, XIcon, MailIcon, UserIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface FoundingFounder {
  id: string;
  email: string;
  roleTitle: string;
  designation?: string;
  name?: string;
  status: "pending" | "active" | "removed";
  invitedAt?: string;
  memberType?: "owner" | "founding_founder";
}

export interface FoundingFoundersTabProps {
  members?: FoundingFounder[];
  isOwner?: boolean;
  onInvite?: (data: { email: string; roleTitle: string }) => void;
  onResend?: (id: string) => void;
  onRevoke?: (id: string) => void;
}

export function FoundingFoundersTab({
  members = [],
  isOwner = false,
  onInvite,
  onResend,
  onRevoke,
}: FoundingFoundersTabProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [roleTitle, setRoleTitle] = useState("");

  const activeMembers = members.filter((m) => m.status !== "removed");

  const handleSubmit = () => {
    if (!email.trim()) return;
    onInvite?.({ email: email.trim(), roleTitle: roleTitle.trim() });
    setEmail("");
    setRoleTitle("");
    setShowDialog(false);
  };

  const handleRevoke = (id: string) => {
    if (!confirm("Are you sure you want to remove this founding founder?")) return;
    onRevoke?.(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Founding Founders</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Invite co-founders to join your cohort for free. They get their own learning progress
            while sharing your startup&apos;s validation data.
          </p>
        </div>
        <AcademicianStartupButton onClick={() => setShowDialog(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Invite Co-Founder
        </AcademicianStartupButton>
      </div>

      {activeMembers.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center">
          <UserIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h4 className="font-medium text-foreground mb-1">No co-founders yet</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Invite your co-founders so they can track their own progress through the cohort.
          </p>
          <AcademicianStartupButton variant="secondary" onClick={() => setShowDialog(true)}>
            <MailIcon className="w-4 h-4 mr-2" />
            Send an Invitation
          </AcademicianStartupButton>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.email}</TableCell>
                  <TableCell>{member.name || "—"}</TableCell>
                  <TableCell>{member.designation ? member.designation.toUpperCase() : (member.roleTitle || "—")}</TableCell>
                  <TableCell>
                    <Badge
                      variant={member.status === "active" ? "default" : "secondary"}
                      className={
                        member.memberType === "owner"
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                          : member.status === "active"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                      }
                    >
                      {member.memberType === "owner" ? "Founder" : member.status === "active" ? "Joined" : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {member.invitedAt || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {member.memberType !== "owner" && (
                      <div className="flex items-center justify-end gap-2">
                        {member.status === "pending" && (
                          <button
                            onClick={() => onResend?.(member.id)}
                            className="p-1.5 text-muted-foreground hover:text-primary rounded-md hover:bg-muted transition-colors"
                            title="Resend invitation"
                          >
                            <SendIcon className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRevoke(member.id)}
                          className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-muted transition-colors"
                          title="Remove"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Founding Founder</DialogTitle>
            <DialogDescription>
              Send an invitation email. They&apos;ll be enrolled in your current cohort for free.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ff-email">Email Address</Label>
              <Input
                id="ff-email"
                type="email"
                placeholder="cofounder@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ff-role">Role Title (optional)</Label>
              <Input
                id="ff-role"
                placeholder="e.g. CTO, COO, Co-Founder"
                value={roleTitle}
                onChange={(e) => setRoleTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>
          </div>
          <DialogFooter>
            <AcademicianStartupButton variant="secondary" onClick={() => setShowDialog(false)}>
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton onClick={handleSubmit} disabled={!email.trim()}>
              <MailIcon className="w-4 h-4 mr-2" />
              Send Invitation
            </AcademicianStartupButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
